import React, { useMemo, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { Grid, Typography, Box, Card } from '@mui/material'
import PropTypes from 'prop-types'
import { useGetPriceSheetQuery } from 'services/priceSheets'
import { useUpdateCustomerMutation } from 'services/customers'
import store from 'state/store'
import { setSnackBar } from 'state/ui'
import { CHURN_ZERO_EVENTS } from 'utils/constants'
import FileUploader from 'components/generic/file-uploader/FileUploader'
import { formatData, formatCustomer, getDefaultValues } from 'components/containers/customers/new-customer/utils'
import { trackEvent } from 'utils/tracking'

const CustomerProfileInfo = ({ customerData }) => {
  const { canUploadFilesCustomer } = useSelector(state => state.featureFlags?.variables || {})
  const supplierConsumerText =
    (customerData?.isSupplier && customerData?.isConsumer && 'Both') ||
    (customerData?.isSupplier && 'Supplier') ||
    (customerData?.isConsumer && 'Consumer') ||
    (customerData?.isCarrier && 'Carrier') ||
    'None'

  const { data: priceSheetData } = useGetPriceSheetQuery(
    { priceSheetId: customerData?.priceSheetId },
    { skip: !customerData?.priceSheetId },
  )
  const priceSheetName = useMemo(() => priceSheetData?.displayName, [priceSheetData])
  const accountManagerGsUser = useMemo(
    () =>
      customerData?.accountManagerGsUser?.accountManagerGsUserId
        ? `${customerData.accountManagerGsUser.accountManagerGsUserFirstName} ${customerData.accountManagerGsUser.accountManagerGsUserLastName}`
        : '---',
    [customerData],
  )

  const [updateCustomer] = useUpdateCustomerMutation()

  const handleError = errorMsg => {
    trackEvent({
      eventName: CHURN_ZERO_EVENTS.UPDATE_CUSTOMER,
      errorMsg,
    })
    store.dispatch(
      setSnackBar({
        snackbarType: 'error',
        snackbarMessage: errorMsg?.data?.error || 'There was an error updating the customer',
      }),
    )
  }

  const handleSuccess = () => {
    trackEvent(CHURN_ZERO_EVENTS.UPDATE_CUSTOMER)

    store.dispatch(
      setSnackBar({
        snackbarType: 'success',
        snackbarMessage: 'Customer successfully updated',
      }),
    )
  }

  const onSubmitData = useCallback(
    async data => {
      const { docs, uploadcareGroupId } = data
      // first use formatCustomer to format the customer data from the GET request
      // then use formatData to reformat the data for the PUT request
      const customer = {
        ...formatData(formatCustomer(getDefaultValues(), customerData)),
        id: customerData.id,
        ...(docs && { docs }),
        ...(uploadcareGroupId && { uploadcareGroupId }),
      }
      await updateCustomer({ customer, customerId: customer.id }).unwrap().then(handleSuccess).catch(handleError)
    },
    [customerData],
  )

  const onUploadFiles = useCallback(
    uploadcareGroupId => {
      onSubmitData({ uploadcareGroupId })
    },
    [onSubmitData],
  )

  const handleRemoveFiles = docs => {
    onSubmitData({ docs })
  }

  return (
    <Box p={3} data-testid="customerData-profile">
      <Grid container rowSpacing={2}>
        <Grid item xs={3}>
          <Typography variant="h5">Profile Information</Typography>
        </Grid>
        <Grid item xs={1.5}>
          <Typography variant="overline" color="textSecondary">
            TYPE
          </Typography>
          <Typography style={{ textTransform: 'capitalize' }}>{customerData?.supplierType || '---'}</Typography>
        </Grid>
        {customerData?.isCarrier ? null : (
          <Grid item xs={1.5}>
            <Typography variant="overline" color="textSecondary">
              PRICE LIST
            </Typography>
            <Typography style={{ textTransform: 'capitalize' }}>{priceSheetName || '---'}</Typography>
          </Grid>
        )}
        <Grid item xs={3}>
          <Typography variant="overline" color="textSecondary">
            VENDOR CLASS
          </Typography>
          <Typography>{customerData?.vendorClass || '---'}</Typography>
        </Grid>
        <Grid item xs={1.5}>
          <Typography variant="overline" color="textSecondary">
            Account Manager
          </Typography>
          <Typography>{accountManagerGsUser}</Typography>
        </Grid>
        <Grid item xs={1.5}>
          <Typography variant="overline" color="textSecondary">
            BUY/NO BUY
          </Typography>
          <Typography>{customerData?.buysForbidden ? 'No Buy' : 'Buy'}</Typography>
        </Grid>
        <Grid item xs={1.5}>
          <Typography variant="overline" color="textSecondary">
            SUPPLIER/CONSUMER
          </Typography>
          <Typography>{supplierConsumerText}</Typography>
        </Grid>
        <Grid item xs={3}>
          <Card
            variant="outlined"
            sx={{
              backgroundColor: 'background.section',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'top',
            }}
          >
            <Box p={2}>
              <Typography variant="overline">NOTES</Typography>
              <Typography variant="h6" sx={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                {customerData?.notes || '---'}
              </Typography>
            </Box>
            {canUploadFilesCustomer && customerData?.id ? (
              <Box p={2}>
                <FileUploader
                  filesData={customerData?.docs || []}
                  onUploadFiles={onUploadFiles}
                  onRemoveFiles={handleRemoveFiles}
                  // tooltipMsg="To attach files, please create the customerData first"
                />
              </Box>
            ) : null}
          </Card>
        </Grid>
      </Grid>
    </Box>
  )
}
CustomerProfileInfo.propTypes = {
  customerData: PropTypes.shape({
    id: PropTypes.string,
    externalId: PropTypes.number,
    supplierType: PropTypes.string,
    vendorClass: PropTypes.string,
    accountManagerGsUser: PropTypes.shape({
      accountManagerGsUserId: PropTypes.string,
      accountManagerGsUserFirstName: PropTypes.string,
      accountManagerGsUserLastName: PropTypes.string,
    }),
    creditBalance: PropTypes.shape({
      amount: PropTypes.number,
      currency: PropTypes.string,
      precision: PropTypes.number,
    }),
    buysForbidden: PropTypes.bool,
    isSupplier: PropTypes.bool,
    isConsumer: PropTypes.bool,
    isCarrier: PropTypes.bool,
    priceSheetId: PropTypes.string,
    notes: PropTypes.string,
    docs: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        url: PropTypes.string,
        createdAt: PropTypes.string,
        uuid: PropTypes.string,
      }),
    ),
  }).isRequired,
}

export default CustomerProfileInfo
